// features/premium/meta.ts — static tables for the premium UI.

import type { IconName } from '@/icons';

export type SubtabId = 'overview' | 'benefits' | 'billing' | 'settings';

export interface Subtab { id: SubtabId; label: string; }

export const SUBTABS: Subtab[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'benefits', label: 'Benefits' },
  { id: 'billing',  label: 'Billing' },
  { id: 'settings', label: 'Settings' },
];

/**
 * Maps each seeded UltraBenefit.title -> a registered <Icon> name, so the page
 * renders SVG icons (never the seed's raw inline-SVG string).
 *
 * 1.23.00: 'Verified Badge' / 'Premium Cherish' / 'Boosted Visibility' were
 * DELETED here (not relabeled) — a checkmark is earned, never bought, and
 * selling feed reach is forbidden (owner ruling). 'Supporter Badge' is the
 * real, distinct, still-2FA-gated replacement.
 */
export const BENEFIT_ICONS: Record<string, IconName> = {
  'Supporter Badge': 'supporter-badge',
  'Cosmetic Flair': 'sparkles',
  'Unlimited Edit Window': 'edit',
  'Post Analytics': 'bar-chart-3',
  'Paid Subscriptions': 'repeat',
  'Paywalled Posts': 'lock',
  "Creators' Hub": 'users',
  'API & Integrations': 'code',
  'Dedicated Support': 'headset',
};

/**
 * Fix-round F10: the ONE declaration of what Pro actually delivers today.
 *
 * A subscriber sees only what they bought: SubscribedView renders exactly
 * these under "active", and everything else in seedUltraBenefits under an
 * explicit not-available-yet heading. Anything gated on a benefit (the
 * Creators' Hub CTA) asks this list, so a not-yet-built benefit can never
 * grow an entry point by accident.
 *
 * Add a title here ONLY once the feature is genuinely shipped to Pro.
 */
export const PRO_ACTIVE_BENEFIT_TITLES: readonly string[] = [
  'Supporter Badge',
  'Cosmetic Flair',
  'Unlimited Edit Window',
  'Post Analytics',
];

/** The benefit that gates the Creators' Hub CTA (see PRO_ACTIVE_BENEFIT_TITLES). */
export const HUB_BENEFIT_TITLE = "Creators' Hub";

export function isProActiveBenefit(title: string): boolean {
  return PRO_ACTIVE_BENEFIT_TITLES.includes(title);
}
